import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { BedrijfSchema, FilterSchema } from '@/api';
import { useLatestBedrijf } from '@/api/queries/bedrijf';
import { useBesluit, useLastVigerendBesluit } from '@/api/queries/besluit';
import { useAllGrondslagenInBesluiten } from '@/api/queries/besluitGrondslag/selectors/useAllGrondsalgenInBesluiten';
import { BesluitCardSkeleton } from '@/components/BesluitCard/BesluitCard';
import { PaginationLinks } from '@/components/PaginationLinks/PaginationLinks';
import { isUserAuthenticatedForRoute } from '@/stores/user/isUserAuthenticatedForRoute';
import { createInString } from '@/utils/createInString';

import BesluitCardInOntwerp from './-components/BesluitCardInOntwerp';
import { BesluitCardNew } from './-components/BesluitCardNew/BesluitCardNew';
import { useHasSelectedOntwerpFilters } from './-components/OntwerpFilters';
import { WijzigingCardNew } from './-components/WijzigingCardNew';
import { omit } from '@/utils/omit/omit';

const searchSchema = z.object({
  p: z.number().optional().default(1),
  type: z.string().optional(),
  vergunningdeel: z.array(z.string()).optional(),
  zaaknummer: z.string().optional(),
});

type SearchParams = z.infer<typeof searchSchema>;

/**
 * Query options
 */
const inOntwerpQueryOptions = (search: SearchParams, bedrijfId?: string) => {
  const filter: FilterSchema[] = [
    {
      model: 'Besluit',
      field: 'Status',
      op: 'in',
      value: createInString(['Ontwerp', 'Bevroren', 'Opmerking']),
    },
    {
      model: 'Besluit',
      field: 'Bedrijf',
      op: 'eq',
      value: bedrijfId ?? '',
    },
  ];

  if (search.type) {
    filter.push({
      model: 'Besluit',
      field: 'Type',
      op: 'eq',
      value: search.type,
    });
  }

  // vergunningdelen: OR filter
  if (search.vergunningdeel?.length) {
    filter.push({
      model: 'BesluitGrondslag',
      field: 'Grondslag',
      op: 'in',
      value: createInString(search.vergunningdeel),
    });
  }

  if (search.zaaknummer) {
    filter.push({
      model: 'Besluit',
      field: 'Zaaknummer',
      op: 'like',
      value: `%${search.zaaknummer.replace(/\s+/g, '%')}%`,
    });
  }

  return useBesluit.getOptions(
    {
      page: search.p,
      size: 10,
      filter,
    },
    {
      enabled: !!bedrijfId,
    }
  );
};

const unfilteredInOntwerpQueryOptions = (bedrijfId?: string) =>
  useBesluit.getOptions(
    {
      page: 1,
      size: 1,
      filter: [
        {
          model: 'Besluit',
          field: 'Status',
          op: 'in',
          value: createInString(['Ontwerp', 'Bevroren', 'Opmerking']),
        },
        {
          model: 'Besluit',
          field: 'Bedrijf',
          op: 'eq',
          value: bedrijfId ?? '',
        },
      ],
    },
    {
      enabled: !!bedrijfId,
    }
  );

/**
 * Route
 */
export const Route = createFileRoute(
  '/$bedrijfLineageId/bedrijf/_layout/ontwerpen'
)({
  validateSearch: searchSchema.parse,
  beforeLoad: async ({ preload }) => {
    await isUserAuthenticatedForRoute({
      preload,
      allowed: ['Vergunningverlener'],
    });

    return {
      getTitle: () => 'Ontwerpen',
    };
  },
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({
    params: { bedrijfLineageId },
    context: { queryClient },
    deps: { search },
  }) => {
    const latestBedrijf = await queryClient.ensureQueryData(
      useLatestBedrijf.getOptions(bedrijfLineageId)
    );

    if (!latestBedrijf.objects?.[0]?.ID) return;

    const hasFilters = Object.values(omit(search, ['p'])).some(Boolean);

    const [ontwerpen] = await Promise.all([
      !hasFilters
        ? queryClient.ensureQueryData(
            inOntwerpQueryOptions(search, latestBedrijf.objects[0].ID)
          )
        : undefined,
      queryClient.ensureQueryData(
        unfilteredInOntwerpQueryOptions(latestBedrijf.objects[0].ID)
      ),
      queryClient.ensureQueryData(
        useLastVigerendBesluit.getOptions({
          bedrijfLineageId,
        })
      ),
    ]);

    if (ontwerpen?.objects?.length) {
      await queryClient.ensureQueryData(
        useAllGrondslagenInBesluiten.queryOptions({
          besluiten: ontwerpen.objects,
        })
      );
    }
  },
  component: OntwerpenComponent,
});

/**
 * Route component
 */
function OntwerpenComponent() {
  const { bedrijfLineageId } = Route.useParams();

  const bedrijf = useQuery(useLatestBedrijf.getOptions(bedrijfLineageId));

  const lastVigerendBesluit = useLastVigerendBesluit({
    bedrijfLineageId: bedrijf?.data?.Lineage,
  });
  const hasVigerendBesluit = !!lastVigerendBesluit.data;

  const besluitenInOntwerp = useQuery(
    unfilteredInOntwerpQueryOptions(bedrijf.data?.ID)
  );

  const hasBesluitenInOntwerp = !!besluitenInOntwerp.data?.objects?.length;

  if (!bedrijf.data) return null;

  return (
    <>
      <section className="flex gap-2 border-b border-borderline-light py-5">
        <BesluitCardNew
          bedrijf={bedrijf.data}
          hasBesluitInOntwerp={hasBesluitenInOntwerp}
          hasVigerendBesluit={hasVigerendBesluit}
        />

        {hasVigerendBesluit ? (
          <WijzigingCardNew bedrijfLineageId={bedrijfLineageId} />
        ) : null}
      </section>

      <section className="py-5">
        <Results
          bedrijf={bedrijf.data}
          hasVigerendBesluit={hasVigerendBesluit}
        />
      </section>
    </>
  );
}

/**
 * Results
 */
const Results = ({
  bedrijf,
  hasVigerendBesluit,
}: {
  bedrijf: BedrijfSchema;
  hasVigerendBesluit: boolean;
}) => {
  const search = Route.useSearch();
  const hasSelectedFilters = useHasSelectedOntwerpFilters();

  const ontwerpen = useQuery(inOntwerpQueryOptions(search, bedrijf.ID));
  const besluitGrondslagen = useAllGrondslagenInBesluiten({
    besluiten: ontwerpen.data?.objects ?? [],
  });

  if (ontwerpen.isLoading || besluitGrondslagen.isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <h2 className="style-h5-bold mb-2 text-gray-800">Ontwerpen</h2>
        <BesluitCardSkeleton type="ontwerp" />
      </div>
    );
  }

  if (!ontwerpen.data?.objects?.length) {
    return (
      <p className="style-body italic text-gray-700">
        {hasSelectedFilters
          ? 'Er zijn geen resultaten met de gekozen filters'
          : 'Er zijn geen ontwerpen voor dit bedrijf'}
      </p>
    );
  }

  return (
    <>
      <h2 className="style-h5-bold mb-2 text-gray-800">Ontwerpen</h2>

      <ul className="flex flex-col gap-2">
        {ontwerpen.data.objects.map((besluit) => {
          return (
            <li key={besluit.ID}>
              <BesluitCardInOntwerp
                headingAs="h3"
                bedrijf={bedrijf}
                besluit={besluit}
                besluitGrondslagen={besluitGrondslagen.data?.objects?.filter(
                  (grondslag) => grondslag.Besluit === besluit.ID
                )}
                hasVigerendBesluit={hasVigerendBesluit}
              />
            </li>
          );
        })}
      </ul>

      {(ontwerpen.data?.num_pages ?? 0) > 1 ? (
        <div className="mt-5 flex justify-center">
          <PaginationLinks
            aria-label="Ontwerpen pagina's"
            renderLink={({ children, page }) => (
              <Link
                to="."
                params={{ bedrijfLineageId: bedrijf.Lineage }}
                search={(old) => ({ ...old, p: page })}
                from={Route.fullPath}
              >
                {children}
              </Link>
            )}
            currentPage={search.p}
            totalPages={ontwerpen.data?.num_pages ?? 1}
          />
        </div>
      ) : null}
    </>
  );
};
